import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import API_BASE_URL from 'Components/API';
import { useTranslation } from 'react-i18next';

const NewsContext = createContext();

export const useNews = () => {
    const context = useContext(NewsContext);
    if (!context) {
        throw new Error('useNews must be used within a NewsProvider');
    }
    return context;
};

export const NewsProvider = ({ children }) => {
    const [sliderData, setSliderData] = useState([]);
    const [newsData, setNewsData] = useState([]);
    const [isLoaded, setIsLoaded] = useState(false);
    const [imageCache, setImageCache] = useState({});
    const [loadingImages, setLoadingImages] = useState({});
    const { i18n } = useTranslation();
    const currentLang = i18n.language;
    const isMounted = useRef(true);
    const objectUrls = useRef([]);
    const initialLoadDone = useRef(false);

    const fetchImage = async (newsId) => {
        if (imageCache[newsId] || loadingImages[newsId]) {
            return;
        }

        setLoadingImages(prev => ({ ...prev, [newsId]: true }));

        try {
            const response = await fetch(`${API_BASE_URL}/Home/GetNewsImage/${newsId}?Lang=${currentLang}`);
            if (!response.ok) throw new Error('Image fetch failed');

            const blob = await response.blob();

            const imageUrl = URL.createObjectURL(blob);
            objectUrls.current.push(imageUrl);

            if (isMounted.current) {
                setImageCache(prev => ({
                    ...prev,
                    [newsId]: {
                        url: imageUrl,
                        contentType: blob.type
                    }
                }));
                setLoadingImages(prev => ({ ...prev, [newsId]: false }));
            }
        } catch (error) {
            console.error(`Error fetching image for news ${newsId}:`, error);
            if (isMounted.current) {
                setLoadingImages(prev => ({ ...prev, [newsId]: false }));
            }
        }
    };

    const fetchImageOnDemand = (newsId) => {
        if (!newsId) return;
        if (!imageCache[newsId] && !loadingImages[newsId]) {
            fetchImage(newsId);
        }
    };

    const fetchSliderData = async () => {
        const url = `${API_BASE_URL}/Home/GetNews?ShowAsSlider=true&PageSize=1&Lang=${currentLang}`;
        try {
            const response = await fetch(url);
            const json = await response.json();
            if (isMounted.current) {
                setSliderData(json.results || []);
                return json.results || [];
            }
        } catch (error) {
            console.error('Error fetching slider data:', error);
            return [];
        }
    };

    const fetchNewsData = async () => {
        const url = `${API_BASE_URL}/Home/GetNews?PageSize=3&Lang=${currentLang}`;
        try {
            const response = await fetch(url);
            const json = await response.json();
            if (isMounted.current) {
                setNewsData(json.results || []);
                return json.results || [];
            }
        } catch (error) {
            console.error('Error fetching news data:', error);
            return [];
        }
    };

    const fetchAllData = async () => {
        try {
            const [sliderResults, newsResults] = await Promise.all([
                fetchSliderData(),
                fetchNewsData()
            ]);

            const allNews = [...sliderResults, ...newsResults];

            const newsWithThumbnails = allNews.filter(news =>
                news.thumbnailHeaderImage && !news.headerImage
            );

            newsWithThumbnails.forEach((news, index) => {
                if (news.id && !imageCache[news.id]) {
                    setTimeout(() => {
                        if (isMounted.current) {
                           fetchImage(news.id);
                        }
                    }, index * 300);
                }
            });

            if (isMounted.current) {
                setIsLoaded(true);
                initialLoadDone.current = true;
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            if (isMounted.current) {
                setIsLoaded(true);
            }
        }
    };

    useEffect(() => {
        return () => {
            isMounted.current = false;
            objectUrls.current.forEach(url => URL.revokeObjectURL(url));
            objectUrls.current = [];
        };
    }, []);

    useEffect(() => {
        fetchAllData();
        // eslint-disable-next-line
    }, [currentLang]);

    const getImageSrc = (news) => {
        if (news.headerImage) {
            return `data:${news.contentType};base64,${news.headerImage}`;
        }
        if (news.thumbnailHeaderImage) {
            return `data:${news.headerImageContentType};base64,${news.thumbnailHeaderImage}`;
        }

        if (imageCache[news.id]) {
            return imageCache[news.id].url;
        }

        return null;
    };

    const clearImageFromCache = (newsId) => {
        setImageCache(prev => {
            const newCache = { ...prev };
            delete newCache[newsId];
            return newCache;
        });
    };

    const value = {
        sliderData,
        newsData,
        isLoaded,
        imageCache,
        loadingImages,
        fetchImageOnDemand,
        getImageSrc,
        clearImageFromCache, 
        setImageCache, 
        setLoadingImages
    };

    return (
        <NewsContext.Provider value={value}>
            {children}
        </NewsContext.Provider>
    );
};